import { parseLineEquation as parseSharedLineEquation, equivalentLines } from "../../../../utils/lineEquation";
﻿// MaturaParallelLinePointOpenTask.js
// Zadanie maturalne – otwarte (geometria analityczna)
// Prosta k dana w postaci kierunkowej.
// Prosta l równoległa do k, przechodząca przez punkt A.
// Punkt (p, yP) należy do prostej l. Wyznacz równanie l oraz p.

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import EquationInput from "../../../../components/inputs/EquationInput";
import OpenTaskScoringHint from "../../../../components/ui/OpenTaskScoringHint";

import FractionInput, {
  formatLatexPreview,
} from "../../../../components/inputs/FractionInput";

// =================== POMOCNICZE ===================

const randInt = (min, max) =>
  Math.floor(Math.random() * (max - min + 1)) + min;

const randNonZeroInt = (min, max) => {
  let x;
  do {
    x = randInt(min, max);
  } while (x === 0);
  return x;
};

const gcd = (a, b) => {
  a = Math.round(a);
  b = Math.round(b);
  while (b !== 0) {
    const t = b;
    b = a % b;
    a = t;
  }
  return Math.abs(a);
};

const simplifyFrac = (num, den) => {
  const g = gcd(num, den);
  return { num: num / g, den: den / g };
};

const fracToLatex = (num, den) => {
  if (den === 1) return `${num}`;
  if (den === -1) return `${-num}`;
  const sign = Math.sign(num) * Math.sign(den);
  const n = Math.abs(num);
  const d = Math.abs(den);
  return `${sign < 0 ? "-" : ""}\\dfrac{${n}}{${d}}`;
};

const approxEqual = (x, y, eps = 1e-4) => Math.abs(x - y) < eps;

const fs = (v) => (v < 0 ? `(${v})` : `${v}`);

// ====== formatowanie równań ======

function formatSlopeLine(a, b) {
  // y = ax + b
  let aPart;
  if (a === 1) aPart = "x";
  else if (a === -1) aPart = "-x";
  else aPart = `${a}x`;

  let res = `y = ${aPart}`;
  if (b > 0) res += ` + ${b}`;
  else if (b < 0) res += ` - ${Math.abs(b)}`;
  return res;
}

// ====== parsowanie dowolnego równania liniowego ======



function parseAnyLineEquation(raw) { return parseSharedLineEquation(raw, { implicitZero: true }); }

// =================== GENERATOR ===================

function generateJune2025Original() {
  return {
    type: "MaturaParallelLinePointOpenTask",
    data: {
      maxPoints: 2,
      a: 5,
      bK: 7,
      bL: -4,
      xA: 0,
      yA: -4,
      yP: 2,
      A: 5,
      B: -1,
      C: -4,
      pNum: 6,
      pDen: 5,
      pVal: 6 / 5,
      anchorMode: "yIntercept",
      askLineEquation: false,
    },
    source: {
      variant: "original",
      exam: "MATURA",
      session: "2025_CZE",
      taskNumber: 22,
    },
  };
}

export function generateTask(generationOptions = {}) {
  if (
    generationOptions.variant === "original" &&
    generationOptions.session === "2025_CZE"
  ) {
    return generateJune2025Original();
  }

  while (true) {
    const a = randNonZeroInt(-5, 5); // współczynnik kierunkowy prostej k
    const bK = randInt(-7, 7); // wyraz wolny prostej k

    let bL;
    do {
      bL = randInt(-7, 7); // wyraz wolny prostej l
    } while (bL === bK); // żeby l ≠ k

    const xA = randInt(-5, 5);
    const yA = a * xA + bL; // A leży na l

    const yP = randInt(-5, 5); // druga współrzędna punktu (p, yP)

    const num = yP - bL;
    const den = a;
    const { num: pNum, den: pDen } = simplifyFrac(num, den);
    const pVal = num / den;

    // lekkie ograniczenie, żeby nie było zbyt paskudnych ułamków
    if (Math.abs(pNum) <= 40 && Math.abs(pDen) <= 12) {
      // prosta l: y = ax + bL → ax - y + bL = 0
      const A = a;
      const B = -1;
      const C = bL;
      return {
        type: "MaturaParallelLinePointOpenTask",
        data: {
          maxPoints: 2,
          a,
          bK,
          bL,
          xA,
          yA,
          yP,
          A,
          B,
          C,
          pNum,
          pDen,
          pVal,
          anchorMode: "point",
          askLineEquation: true,
        },
      };
    }
  }
}

// =================== TREŚĆ ZADANIA ===================

export function renderTask({ data }) {
  const { a, bK, bL, xA, yA, yP, anchorMode, askLineEquation } = data;

  const kEq = formatSlopeLine(a, bK);

  return (
    <div className="space-y-2" style={{ textAlign: "left" }}>
      <p>
        W kartezjańskim układzie współrzędnych{" "}
        <InlineMath math="(x,y)" /> dana jest prosta{" "}
        <InlineMath math="k" /> o równaniu{" "}
      </p>
      <BlockMath math={kEq} />
      {anchorMode === "yIntercept" ? (
        <p>
          Prosta <InlineMath math="l" /> jest równoległa do prostej{" "}
          <InlineMath math="k" /> i przecina oś <InlineMath math="Oy" /> w
          punkcie <InlineMath math={`(0, ${bL})`} />.
        </p>
      ) : (
        <p>
          Prosta <InlineMath math="l" /> jest równoległa do prostej{" "}
          <InlineMath math="k" /> i przechodzi przez punkt{" "}
          <InlineMath math={`A = (${xA}, ${yA})`} />.
        </p>
      )}
      <p>
        Punkt o współrzędnych{" "}
        <InlineMath math={`(p, ${yP})`} /> należy do prostej{" "}
        <InlineMath math="l" />.
      </p>
      <p>
        {askLineEquation ? (
          <>
            Wyznacz równanie prostej <InlineMath math="l" /> oraz oblicz{" "}
            <InlineMath math="p" />.
          </>
        ) : (
          <>
            Oblicz <InlineMath math="p" />. Zapisz obliczenia.
          </>
        )}
      </p>
    </div>
  );
}

// =================== INPUT (2 pola) ===================

export function renderInput({ data }, userAnswer, onChange, disabled) {
  const ua = userAnswer || {};

  return (
    <div className="space-y-3">
      <OpenTaskScoringHint variant="independent" />
      {data.askLineEquation ? (
        <div>
          <p>
            Równanie prostej <InlineMath math="l" /> (np.{" "}
            <InlineMath math="2x - y + 4 = 0" /> lub{" "}
            <InlineMath math="y = 2x + 4" />):
          </p>
          <EquationInput
            value={ua.line || ""}
            onChange={(val) => onChange({ ...ua, line: val })}
            disabled={disabled}
            placeholder="np. 2x - y + 4 = 0"
          />
        </div>
      ) : null}

      <div className="flex items-center gap-2">
        <InlineMath math="p =" />
        <FractionInput
          value={ua.p || ""}
          onChange={(val) => onChange({ ...ua, p: val })}
          disabled={disabled}
          label="p"
        />
      </div>
    </div>
  );
}

// =================== CHECK (0–2 pkt + parts) ===================

export function checkAnswer({ data }, userAnswer) {
  const ua = userAnswer || {};
  const { A, B, C, pVal, askLineEquation } = data;

  // 1) równanie prostej l
  const parsed = parseAnyLineEquation(ua.line);
  let lineOk = false;

  lineOk = equivalentLines(parsed, { A, B, C });

  // 2) wartość p
  const pObj = ua.p;
  const pOk =
    pObj && typeof pObj.parsed === "number" && approxEqual(pObj.parsed, pVal);

  if (!askLineEquation) {
    return {
      isCorrect: pOk,
      score: pOk ? 2 : 0,
      maxScore: 2,
      parts: {
        "wartość p": pOk,
      },
    };
  }

  const score = (lineOk ? 1 : 0) + (pOk ? 1 : 0);

  return {
    isCorrect: score === 2,
    score,
    maxScore: 2,
    parts: {
      "równanie prostej l": lineOk,
      "wartość p": pOk,
    },
  };
}

// =================== POPRAWNA ODPOWIEDŹ ===================

export function renderAnswer({ data }) {
  const { a, bL, pNum, pDen } = data;

  const lSlope = formatSlopeLine(a, bL);
  const fracP = fracToLatex(pNum, pDen);

  return (
    <div
      style={{
        marginTop: 16,
        display: "grid",
        rowGap: 4,
        lineHeight: 1.35,
        textAlign: "left",
      }}
    >
      <InlineMath math={lSlope} />
      <InlineMath math={`p = ${fracP}`} />
    </div>
  );
}

// =================== WYJAŚNIENIE KROK PO KROKU ===================

export function renderExplanation({ data }) {
  const { a, bL, xA, yA, yP, pNum, pDen, anchorMode } = data;

  const aLatex = `${a}`;
  const bLLatex = `${bL}`;
  const pLatex = fracToLatex(pNum, pDen);

  const lSlope = formatSlopeLine(a, bL);

  const num = yP - bL;
  const den = a;
  const fracRaw = fracToLatex(num, den);

  return (
    <div className="space-y-4" style={{ textAlign: "left" }}>
      <div>
        <strong>Krok 1.</strong> Prosta{" "}
        <InlineMath math="k" /> ma współczynnik kierunkowy{" "}
        <InlineMath math={`a = ${aLatex}`} />. Prosta{" "}
        <InlineMath math="l" /> jest równoległa do{" "}
        <InlineMath math="k" />, więc ma ten sam współczynnik kierunkowy.
      </div>

      <div>
        <strong>Krok 2.</strong>{" "}
        {anchorMode === "yIntercept" ? (
          <>
            Punkt przecięcia z osią <InlineMath math="Oy" /> ma współrzędne{" "}
            <InlineMath math={`(0, ${bL})`} />, więc wyraz wolny jest równy{" "}
            <InlineMath math={`b = ${bLLatex}`} />.
          </>
        ) : (
          <>
            Równanie prostej <InlineMath math="l" /> zapisujemy w postaci{" "}
            <InlineMath math="y = ax + b" /> i korzystamy z faktu, że
            przechodzi przez punkt{" "}
            <InlineMath math={`A = (${xA}, ${yA})`} />:
            <BlockMath math={`y_A = a x_A + b`} />
            <BlockMath
              math={`${yA} = ${aLatex}\\cdot ${fs(
                xA
              )} + b \\quad \\Rightarrow \\quad b = ${yA} - ${fs(a)}\\cdot ${fs(
                xA
              )} = ${bLLatex}`}
            />
          </>
        )}
        {" "}Otrzymujemy:
        <BlockMath math={lSlope} />
      </div>

      <div>
        <strong>Krok 3.</strong> Punkt{" "}
        <InlineMath math={`(p, ${yP})`} /> leży na prostej{" "}
        <InlineMath math="l" />, więc spełnia jej równanie:
        <BlockMath math={`${yP} = ${aLatex}p + ${fs(bL)}`} />
        Przenosimy{" "}
        <InlineMath math={bLLatex} /> na drugą stronę:
        <BlockMath math={`${aLatex}p = ${yP} - ${fs(bL)}`} />
        i dzielimy przez{" "}
        <InlineMath math={aLatex} />:
        <BlockMath
          math={`p = \\dfrac{${yP} - ${fs(bL)}}{${aLatex}} = ${fracRaw} = ${pLatex}`}
        />
      </div>

      <div>
        Zatem{" "}
        <InlineMath math={`p = ${pLatex}`} />, a równanie prostej{" "}
        <InlineMath math="l" /> może być zapisane w postaci{" "}
        <InlineMath math={lSlope} />.
      </div>
    </div>
  );
}

// =================== ODPOWIEDŹ UŻYTKOWNIKA ===================

export function renderUserAnswer(userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object") {
    return <i>(brak odpowiedzi)</i>;
  }

  const line = (userAnswer.line || "").trim();
  const pObj = userAnswer.p;

  return (
    <div className="space-y-2" style={{ textAlign: "left" }}>
      <div>
        <strong>Twoje równanie prostej </strong>
        <InlineMath math="l" />:
        <div style={{ marginTop: "0.5rem" }}>
          {line ? <InlineMath math={line} /> : <i>(brak)</i>}
        </div>
      </div>
      <div>
        <strong>Twoja wartość </strong>
        <InlineMath math="p" />
        <strong>:</strong>{" "}
        {pObj && pObj.raw ? (
          <InlineMath math={formatLatexPreview(pObj.raw, "p")} />
        ) : (
          <i>(brak)</i>
        )}
      </div>
    </div>
  );
}

export default {
  generateTask,
  renderTask,
  renderInput,
  checkAnswer,
  renderAnswer,
  renderExplanation,
  renderUserAnswer,
};
